import { action, reaction } from 'mobx';
import i18next from 'i18next';
import Alert from 'react-s-alert';

import { App } from 'core/index';
import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';
import { SetToken, tokenType } from 'core/jwt';
import { WizardStore } from 'libs/wizards/app';
import { ProfileModel } from 'apps/profile/app';
import { UserApp } from 'apps/user/app';
import { TagsApp } from 'apps/tag/app';
import { clientStore } from 'apps/client/app';
import { raiseUserError } from 'apps/utils';
import { modalStore } from 'apps/modals/app';

import { conf } from 'apps/profile/config.yaml';

import { conf as globalConf } from 'apps/config.yaml';
import { getUsernameByEmail } from 'utils/formatStrings';

const { siteName } = globalConf;

const formOverrides = {
  cadrooms: {
    formTitle: 'Sign up to CAD Rooms',
    proTip:
      'you can sign up and try CAD Rooms for free. No credit card is required. Sign up takes less than 2 minutes.',
  },
};

export class SignUpApp extends App {
  static registryName = 'SignUpApp';
  static AppProps = {
    user: UserApp,
    skills: TagsApp,
  };

  static Model = ProfileModel;

  isModal = false;

  init(isModal = false) {
    let wizardConf = conf;
    const alternate = routerStore.params.get('alt');
    const override = formOverrides[alternate];
    if (override) {
      wizardConf = {
        ...wizardConf,
        simpleSignUp: { ...wizardConf.simpleSignUp, ...override },
      };
    }
    this.wizard = new WizardStore(this, wizardConf, 'signUpWizard', {
      createProfile: this.CreateProfile,
    });

    if (window.location.href.split('id=')[1]) {
      try {
        const email = atob(
          decodeURIComponent(window.location.href.split('id=')[1]),
        );
        this.user.email = email;
        this.user.username = `@${getUsernameByEmail(email)}`;
      } catch (err) {
        console.error(err);
      }
    }
    this.isModal = isModal;

    const disposer = reaction(
      () => this.user.email,
      email => {
        if (!email) return;
        const separatorIndex = email.indexOf('@');
        if (separatorIndex === -1) return;
        this.user.username = `@${email.substring(0, separatorIndex)}`;

        // clear any previous validation errors since the value is changing
        // this should be automatic since the target changed, but it is not because apparently
        // the error only gets cleared if the modification comes as a form onChange from the UI
        this.wizard.activeForm.fieldStores.get('username').serverValidation =
          null;
      },
    );
    this.disposers.push(disposer);
  }

  @action onSysError = () => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    // TODO: Distinguish case where not connected to internet / where there's a network error
    Alert.error(
      i18next.t('Hmm... We failed to create your Profile. Please try again.'),
    );
  };

  @action onUserErr = err => {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);
    this.wizard.ToErrorForm(err);
    raiseUserError(err, 'create', 'Profile');
  };

  @action handleSignUpResult = result => {
    modalStore.Close();
    analytics.sendEvent('complete-signup');
    analytics.sendEvent(
      'SubmitApplication',
      {
        value: 25,
        currency: 'EUR',
        intent: result.profile.intent,
        profession: result.profile.userType,
      },
      { twitter: true, facebook: true },
    );
    if (this.manager.signUpInput.startFreeTrial) {
      analytics.sendEvent('start-free-trial', { space: this.user.username });
      analytics.sendEvent(
        'StartTrial',
        { value: 7, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
    }

    analytics.linkedinPixel('1169145');
    // TODO: display message saying to check e-mail for validation
    const username = this.preferredName
      ? this.preferredName
      : this.user.username;
    Alert.success(
      i18next.t('Welcome to {{siteName}}!', {
        siteName,
        username,
      }),
    );
    const { params } = this.manager;
    const next = params && params.get('next');
    const billing = next && next === 'billing';
    if (billing) {
      analytics.sendEvent(
        'InitiateCheckout',
        { value: 5, currency: 'EUR' },
        { twitter: true, facebook: true },
      );
      analytics.linkedinPixel('1169161');
      routerStore.router.navigate('profile', {
        profile: result.profile.username,
        subscribe: true,
      });
      return;
    }
    if (!this.isModal) {
      const match = next ? routerStore.router.matchUrl(next) : null;
      if (match) {
        routerStore.router.navigate(match.name, match.params);
      } else {
        routerStore.router.navigate('home');
      }
    }
  };

  async onSuccess(result) {
    this.wizard.activeForm.SetPosting(false);
    clientStore.SetPosting(false);

    const { token, refreshToken, profile } = result;
    const { user } = profile || {};

    const isFromMakePart = routerStore.previousRoute?.name === 'make-part';

    const next = clientStore.next();

    // if the email status is confirmed, or the user comes from the make part page
    // we can set the token and redirect to the home page
    if (user?.emailStatus === 'CONFIRMED' || isFromMakePart) {
      SetToken(token);
      SetToken(refreshToken, tokenType.refreshToken);

      clientStore.SetSignedInProfile(ProfileModel.Load(profile));

      clientStore.QueryAvailableSpaces(true);

      if (next) {
        clientStore.routerStore.navigateNext();
      } else {
        routerStore.router.navigate('home');
      }
    } else {
      routerStore.router.navigate('onboarding.checkEmail', {
        email: result.profile.email,
      });
    }

    this.wizard.clearDraft();
  }

  @action CreateProfile = () => {
    if (this.wizard.activeForm.isValid) {
      this.wizard.activeForm.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.SignUp({
        onSysErr: this.onSysError,
        onUserErr: this.onUserErr,
        onSuccess: this.onSuccess.bind(this),
      });
    } else {
      this.wizard.activeForm.alert();
    }
  };
}
